import React, { useEffect, useMemo, useState } from "react";
import { NutritionMenuData } from "../providers/ConfigProvider";
import { LayoutRectangle, View } from "react-native";
import { NutritionDiningStation } from "./NutritionDiningStation";
import { findIndex, max, min, sortBy } from "lodash";
import { ErrorScreen } from "../screens/ErrorScreen";
import { useTranslation } from "react-i18next";
import { useTheme } from "../providers/ThemeProvider";

const baseMinWidth = 520; // min station width
const minColumns = 1;
const maxColumns = 3;

export const NutritionMenu = ({
  onLoopComplete,
  data,
}: {
  onLoopComplete: () => void;
  data: NutritionMenuData | null;
}) => {
  const { t } = useTranslation("NutritionMenu");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [watchStationIndex, setWatchStationIndex] = useState(0);
  const [dimensions, setDimensions] = useState<LayoutRectangle>();
  const [itemHeight, setItemHeight] = useState<number>();

  const theme = useTheme();
  const minWidth = theme.scaled(baseMinWidth);

  const pages = useMemo(() => {
    if (dimensions == null || data == null) return [];

    const stations = sortBy(
      data.menu.mealMenuDiningStations,
      (s) => s.position,
    );
    const numberColumns = max([
      minColumns,
      min([maxColumns, Math.floor(dimensions.width / minWidth)]) as number,
    ]) as number;

    return Array.from(
      { length: Math.ceil(stations.length / numberColumns) },
      () => stations.splice(0, numberColumns),
    );
  }, [data, dimensions, minWidth]);

  const currentPage = pages[currentPageIndex];

  useEffect(() => {
    if (currentPage == null) return;

    const pageItemsCount = currentPage.map((p) => p.menuItemAppearances.length);
    const largestCount = max(pageItemsCount);
    setWatchStationIndex(findIndex(pageItemsCount, (c) => c === largestCount));
  }, [currentPage, pages]);

  if (data == null) {
    return <ErrorScreen message={t("Failed to fetch menu")} />;
  }

  const { includeDescriptions, includeImages, includeMacros } = data;

  return (
    <View
      style={{ flexGrow: 1, alignItems: "center", justifyContent: "center" }}
    >
      <View
        style={{
          flexDirection: "row",
          flexGrow: 1,
          width: "100%",
          paddingHorizontal: theme.spacing.small,
          gap: theme.spacing.small,
        }}
      >
        {dimensions == null ? (
          <View
            style={{ flexGrow: 1 }}
            onLayout={(ev) => setDimensions(ev.nativeEvent.layout)}
          />
        ) : (
          currentPage?.map((diningStation, index) => (
            <NutritionDiningStation
              key={diningStation.id}
              style={{ marginHorizontal: theme.spacing.none }}
              data={{
                diningStation,
                includeDescriptions,
                includeImages,
                includeMacros,
              }}
              menuItemHeight={itemHeight}
              onItemHeightChange={(height, forced) => {
                setItemHeight((prev) =>
                  forced ? height : Math.max(prev ?? 0, height),
                );
              }}
              onLoopComplete={() => {
                if (index === watchStationIndex) {
                  let nextIndex = currentPageIndex + 1;
                  if (nextIndex >= pages.length) {
                    nextIndex = 0;
                    onLoopComplete();
                  }
                  setCurrentPageIndex(nextIndex);
                }
              }}
            />
          ))
        )}
      </View>
    </View>
  );
};
